import {Tooltip, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';

// This component shows a tooltip if the text overflows its container.
// Use the optional `component` prop to render as MUI Link (e.g. for breadcrumb links).
const OverflowTooltip = (props) => {
  const {
    text,
    children,
    passthroughProps,
    tooltipProps = {},
    component: Component,
  } = props;
  const textElementRef = useRef(null);
  const [hoverStatus, setHover] = useState(false);

  // Compare the rendered width versus scroll width to detect overflow
  const compareSize = () => {
    const el = textElementRef.current;
    // In JS, el might be null--if so, we can't check overflow
    if (!el) return;
    // Make sure el supports scrollWidth/clientWidth (i.e., is an Element)
    // Guard to ensure el is an Element with scrollWidth/clientWidth on it
    if (el && el instanceof HTMLElement) {
      setHover(el.scrollWidth > el.clientWidth);
    }
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', compareSize);
    };
    // Only run on mount/unmount
  }, []);

  const truncationSx = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...(passthroughProps && passthroughProps.sx ? passthroughProps.sx : {}),
  };

  const content = children ?? text;

  return (
    <Tooltip
      disableHoverListener={!hoverStatus}
      placement="top"
      sx={{fontSize: '2em'}}
      title={text}
      {...tooltipProps}
    >
      {Component ? (
        <Component ref={textElementRef} {...passthroughProps} sx={truncationSx}>
          {content}
        </Component>
      ) : (
        <Typography
          ref={textElementRef}
          {...passthroughProps}
          sx={truncationSx}
        >
          {content}
        </Typography>
      )}
    </Tooltip>
  );
};

OverflowTooltip.propTypes = {
  text: PropTypes.string.isRequired,
  tooltipProps: PropTypes.object,
  passthroughProps: PropTypes.shape({
    sx: PropTypes.object,
  }),
  children: PropTypes.node,
  component: PropTypes.elementType,
};

export default OverflowTooltip;
